import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import {
  ClipboardCheck,
  Layers,
  Package,
  Paintbrush,
  Truck,
} from 'lucide-react';
import {
  homeScrollItem,
  homeScrollViewport,
} from '../../utils/homeScrollMotion';
import styles from './ManufacturingProcessSection.module.css';

const CYCLE_MS = 3600;

const steps = [
  {
    step: '01',
    title: 'Raw Material Selection',
    detail: 'FSC-certified fibre and virgin pulp — screened before reel intake.',
    icon: Layers,
  },
  {
    step: '02',
    title: 'Coating Process',
    detail: 'PE and food-grade coating applied on precision converting lines.',
    icon: Paintbrush,
  },
  {
    step: '03',
    title: 'Quality Testing',
    detail: 'GSM, surface finish and food-contact grades checked batch-wise.',
    icon: ClipboardCheck,
  },
  {
    step: '04',
    title: 'Packaging',
    detail: 'Reel wrapping, moisture-safe sealing and labelled dispatch prep.',
    icon: Package,
  },
  {
    step: '05',
    title: 'Dispatch',
    detail: 'Pan-India logistics with repeat bulk schedules you can plan on.',
    icon: Truck,
  },
];

const PATH_D =
  'M 40 36 C 140 8, 240 64, 340 36 S 540 8, 640 36 840 64, 960 36';

const flowViewport = { once: true, amount: 0.3 };

const nodeReveal = {
  hidden: { scale: 0.5, opacity: 0 },
  visible: (i) => ({
    scale: 1,
    opacity: 1,
    transition: {
      delay: 0.25 + i * 0.14,
      type: 'spring',
      stiffness: 300,
      damping: 22,
    },
  }),
};

const copyReveal = {
  hidden: { opacity: 0, y: 20 },
  visible: (i) => ({
    opacity: 1,
    y: 0,
    transition: {
      delay: 0.4 + i * 0.14,
      duration: 0.5,
      ease: [0.22, 1, 0.36, 1],
    },
  }),
};

const ManufacturingProcessSection = () => {
  const [active, setActive] = useState(0);
  const [hovered, setHovered] = useState(null);
  const [inView, setInView] = useState(false);

  const current = hovered ?? active;

  const selectStep = (index) => {
    setActive(index);
    setHovered(null);
  };

  useEffect(() => {
    if (!inView || hovered !== null) return undefined;

    const timer = window.setInterval(() => {
      setActive((prev) => (prev + 1) % steps.length);
    }, CYCLE_MS);

    return () => window.clearInterval(timer);
  }, [inView, hovered]);

  return (
    <section
      className={styles.section}
      aria-labelledby="manufacturing-process-title"
    >
      <div className={styles.container}>
        <motion.header
          className={styles.header}
          variants={homeScrollItem}
          initial="hidden"
          whileInView="visible"
          viewport={homeScrollViewport}
        >
          <p className={styles.kicker}>Production flow</p>
          <h2 id="manufacturing-process-title" className={styles.title}>
            Our <span className={styles.highlight}>Manufacturing Process</span>
          </h2>
          <p className={styles.subtitle}>
            From fibre intake to sealed dispatch — every stage runs under batch-wise control at our Meerut facility.
          </p>
          <div className={styles.headerRule} aria-hidden="true">
            <span />
            <span />
          </div>
        </motion.header>

        <motion.div
          className={styles.track}
          variants={{ hidden: {}, visible: {} }}
          initial="hidden"
          whileInView="visible"
          viewport={flowViewport}
          onViewportEnter={() => setInView(true)}
          onViewportLeave={() => setInView(false)}
        >
          <svg
            className={styles.pathSvg}
            viewBox="0 0 1000 72"
            preserveAspectRatio="none"
            aria-hidden="true"
          >
            <defs>
              <linearGradient id="mfgPathGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                <stop offset="0%" stopColor="#1b8b3d" />
                <stop offset="72%" stopColor="#2aad52" />
                <stop offset="100%" stopColor="#3cb371" />
              </linearGradient>
              <filter id="mfgPathGlow" x="-20%" y="-80%" width="140%" height="260%">
                <feGaussianBlur stdDeviation="2" result="blur" />
                <feMerge>
                  <feMergeNode in="blur" />
                  <feMergeNode in="SourceGraphic" />
                </feMerge>
              </filter>
            </defs>

            <path className={styles.pathBase} d={PATH_D} pathLength="1" />
            <motion.path
              className={styles.pathActive}
              d={PATH_D}
              pathLength="1"
              variants={{
                hidden: { opacity: 0 },
                visible: {
                  opacity: 1,
                  transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] },
                },
              }}
            />

            {inView && (
              <circle className={styles.pathRunner} r="5">
                <animateMotion
                  dur="7s"
                  repeatCount="indefinite"
                  path={PATH_D}
                  calcMode="linear"
                />
              </circle>
            )}
          </svg>

          <ol className={styles.steps} aria-label="Manufacturing process steps">
            {steps.map((item, index) => {
              const Icon = item.icon;
              const isActive = current === index;

              return (
                <li
                  key={item.title}
                  className={`${styles.step} ${isActive ? styles.stepActive : ''}`}
                  tabIndex={0}
                  onMouseEnter={() => setHovered(index)}
                  onMouseLeave={() => setHovered(null)}
                  onFocus={() => setHovered(index)}
                  onBlur={() => setHovered(null)}
                  onClick={() => selectStep(index)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      selectStep(index);
                    }
                  }}
                >
                  <motion.div
                    className={styles.node}
                    custom={index}
                    variants={nodeReveal}
                    animate={isActive ? { scale: 1.1, y: -2 } : { scale: 1, y: 0 }}
                    transition={{ type: 'spring', stiffness: 400, damping: 20 }}
                  >
                    <span className={styles.nodeRing} aria-hidden="true" />
                    <span className={styles.nodePulse} aria-hidden="true" />
                    <Icon size={20} strokeWidth={2.2} aria-hidden="true" />
                  </motion.div>

                  <motion.div
                    className={styles.stepCopy}
                    custom={index}
                    variants={copyReveal}
                  >
                    <h3 className={styles.stepTitle}>
                      <span className={styles.stepNum}>{item.step}</span>
                      {item.title}
                    </h3>
                    <motion.span
                      className={styles.stepUnderline}
                      animate={{ scaleX: isActive ? 1 : 0 }}
                      transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
                      aria-hidden="true"
                    />
                    <p className={styles.stepDetail}>{item.detail}</p>
                  </motion.div>
                </li>
              );
            })}
          </ol>
        </motion.div>
      </div>
    </section>
  );
};

export default ManufacturingProcessSection;
